/**
 * SkipLink Component
 * "Skip to Content" accessibility link
 * Hidden until focused via keyboard navigation
 */

import React from 'react';
import { styled } from '@mui/material/styles';

export interface SkipLinkProps {
  /**
   * Target element ID to skip to (without #)
   */
  targetId?: string;
  
  /**
   * Link text
   */
  children?: React.ReactNode;
}

const StyledLink = styled('a')(({ theme }) => ({
  position: 'absolute',
  top: '-100px',
  left: '16px',
  zIndex: 9999,
  padding: '12px 24px',
  backgroundColor: theme.palette.mode === 'dark' ? '#1f2937' : '#ffffff',
  color: theme.palette.mode === 'dark' ? '#f3f4f6' : '#1f2937',
  borderRadius: '8px',
  fontWeight: 600,
  fontSize: '14px',
  textDecoration: 'none',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  border: `2px solid ${theme.palette.mode === 'dark' ? '#667eea' : '#667eea'}`,
  transition: 'top 200ms ease-in-out',
  
  '&:focus': {
    top: '16px',
    outline: 'none',
  },
  
  '&:focus-visible': {
    outline: '3px solid #667eea',
    outlineOffset: '2px',
  },
  
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? '#374151' : '#f3f4f6',
  },
}));

/**
 * SkipLink
 * Provides keyboard users a way to skip navigation and jump to main content
 * Hidden by default, appears on focus
 */
export const SkipLink: React.FC<SkipLinkProps> = ({
  targetId = 'main-content',
  children = 'Skip to main content',
}) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      target.setAttribute('tabindex', '-1');
      target.focus();
      target.removeAttribute('tabindex');
    }
  };

  return (
    <StyledLink href={`#${targetId}`} onClick={handleClick}>
      {children}
    </StyledLink>
  );
};

export default SkipLink;
